import {QueryClient, useMutation, useQueryClient} from '@tanstack/react-query';
import {useParams} from 'react-router-dom';
import proposalUpdaters from '@/api/updaters/proposals';
import type {CampaignCreatorApiResponse} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import {addApplicationExpirationExtension} from '@/views/campaigns/creators-tab/utils/isApplicationExpiringSoon';
import {CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY} from '@/hooks/queries/use-campaign-v2-creators-query';

export type ExtendExpirationDateVariables = {
  paidGigId: string;
  currentExpiresAt?: string | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null;
  return value as Record<string, unknown>;
}

function extractExpiresAt(payload: unknown): string | null {
  const record = asRecord(payload);
  if (!record) return null;
  const nested = asRecord(record.data);
  const source = nested ?? record;
  if (typeof source.expiresAt === 'string') return source.expiresAt;
  if (typeof source.expires_at === 'string') return source.expires_at;
  return null;
}

function patchExpirationOnCreatorsCache(
  queryClient: QueryClient,
  campaignId: string,
  paidGigId: string,
  nextExpiresAt: string | null
) {
  queryClient.setQueriesData<{
    pages?: {data: CampaignCreatorApiResponse[]}[];
    pageParams?: unknown[];
  }>(
    {
      queryKey: [CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY, {campaignId}],
      exact: false,
    },
    (old) => {
      if (!old?.pages) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          data: page.data.map((creator) => {
            if (creator.id !== paidGigId && creator.paidGigId !== paidGigId) {
              return creator;
            }
            return {
              ...creator,
              expirationHasBeenExtended: true,
              expiresAt: nextExpiresAt ?? creator.expiresAt ?? null,
            };
          }),
        })),
      };
    }
  );
}

function useExtendExpirationDate() {
  const {campaignId} = useParams<{campaignId: string}>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['proposals', 'extendExpirationDate'],
    mutationFn: ({paidGigId}: ExtendExpirationDateVariables) =>
      proposalUpdaters.extendExpirationDate({paidGigId}),
    onSuccess: async (data, variables) => {
      if (!campaignId) return;
      await queryClient.cancelQueries({
        queryKey: [CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY, {campaignId}],
        exact: false,
      });
      const nextExpiresAt =
        extractExpiresAt(data) ??
        addApplicationExpirationExtension(variables.currentExpiresAt);
      patchExpirationOnCreatorsCache(
        queryClient,
        campaignId,
        variables.paidGigId,
        nextExpiresAt
      );
    },
  });
}

export default useExtendExpirationDate;
